<template>
  <!--
    Seletor de cliente renderizado DENTRO da conversa.

    Ele aparece quando a pergunta escolhida precisa de um cliente e ainda nao ha
    contexto. Antes disso as sugestoes eram enviadas ao pe da letra — "Qual o
    limite do cliente X?" fazia a Clara procurar um cliente chamado X.
  -->
  <div
    class="picker"
    data-test="clara-customer-picker"
  >
    <p class="picker__title">Sobre qual cliente?</p>

    <v-text-field
      ref="input"
      v-model="term"
      dense
      outlined
      hide-details
      autofocus
      class="picker__field"
      placeholder="Nome ou CPF/CNPJ"
      data-test="clara-customer-picker-input"
      @input="onType"
      @keydown.esc="$emit('cancel')"
    >
      <template #prepend-inner>
        <PhMagnifyingGlass
          :size="15"
          class="picker__field-icon"
        />
      </template>
    </v-text-field>

    <div
      v-if="loading"
      class="picker__hint"
    >
      Buscando...
    </div>

    <div
      v-else-if="showEmpty"
      class="picker__hint"
      data-test="clara-customer-picker-empty"
    >
      Nenhum cliente encontrado para “{{ term }}”.
    </div>

    <ul
      v-else-if="results.length"
      class="picker__list"
    >
      <li
        v-for="customer in results"
        :key="customer.id"
      >
        <button
          type="button"
          class="picker__option"
          data-test="clara-customer-picker-option"
          @click="$emit('select', customer)"
        >
          <span class="picker__option-name">{{ customer.name }}</span>
          <span
            v-if="customer.document"
            class="picker__option-doc"
          >{{ customer.document }}</span>
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="picker__cancel"
      data-test="clara-customer-picker-cancel"
      @click="$emit('cancel')"
    >
      Cancelar
    </button>
  </div>
</template>

<script>
import CustomerApi from '@/api/customer/customer-api';
import utilsStorage from '@/utils/storage';

/** Abaixo de 2 caracteres a busca traria meio cadastro e nada util. */
const MIN_TERM = 2;
const DEBOUNCE_MS = 350;
const MAX_RESULTS = 6;

export default {
  name: 'ClaraCustomerPicker',

  data() {
    return {
      term: '',
      results: [],
      loading: false,
      searched: false,
      customerApi: null
    };
  },

  computed: {
    showEmpty() {
      return this.searched && !this.results.length && this.term.trim().length >= MIN_TERM;
    }
  },

  created() {
    this.customerApi = new CustomerApi(utilsStorage.getUserOnStorage());

    /* Fora do `data` de propósito: são controles internos, não estado de tela.
       No data virariam reativos à toa e disparariam re-render a cada tecla.
       `requestSeq` descarta resposta fora de ordem — uma busca lenta por "ma"
       não pode sobrescrever o resultado já exibido de "maria". */
    this.debounceId = null;
    this.requestSeq = 0;
  },

  beforeDestroy() {
    clearTimeout(this.debounceId);
  },

  methods: {
    onType() {
      clearTimeout(this.debounceId);
      this.searched = false;

      if (this.term.trim().length < MIN_TERM) {
        this.results = [];
        this.loading = false;

        return;
      }

      this.loading = true;
      this.debounceId = setTimeout(this.search, DEBOUNCE_MS);
    },

    async search() {
      const seq = ++this.requestSeq;
      const term = this.term.trim();

      try {
        const response = await this.customerApi.findAll({
          searchText: term,
          quantity: MAX_RESULTS,
          page: 1
        });

        if (seq !== this.requestSeq) return;

        this.results = (response.data || []).slice(0, MAX_RESULTS).map(this.toOption);
      } catch (error) {
        if (seq !== this.requestSeq) return;

        this.results = [];
      } finally {
        if (seq === this.requestSeq) {
          this.loading = false;
          this.searched = true;
        }
      }
    },

    toOption(customer) {
      return {
        id: customer.id,
        name: customer.name || customer.customerNameOrCompanyName || `Cliente ${customer.id}`,
        document: customer.cpfCnpj || null
      };
    }
  }
};
</script>

<style scoped>
.picker {
  display: flex;
  flex-direction: column;
  gap: var(--tc-space-3);
  padding: var(--tc-space-4);
  margin-bottom: var(--tc-space-3);
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-md);
  background: var(--clara-tint);
}

.picker__title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--clara-text);
}

.picker__field-icon {
  margin-top: 2px;
  opacity: 0.6;
}

.picker__hint {
  font-size: 0.8rem;
  opacity: 0.75;
}

.picker__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.picker__option {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: var(--tc-space-2) var(--tc-space-3);
  border: none;
  border-radius: var(--tc-radius-sm);
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
  transition: background var(--tc-transition);
}

.picker__option:hover,
.picker__option:focus-visible {
  background: var(--tc-surface);
  outline: none;
}

.picker__option:focus-visible {
  outline: 2px solid var(--clara-text);
  outline-offset: -2px;
}

.picker__option-name {
  font-size: 0.85rem;
  font-weight: 500;
}

.picker__option-doc {
  font-size: 0.72rem;
  opacity: 0.7;
}

.picker__cancel {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  cursor: pointer;
  opacity: 0.75;
  color: inherit;
  text-decoration: underline;
}

.picker__cancel:hover {
  opacity: 1;
}
</style>
